import * as ExcelJS from "exceljs";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";
import { AchatArticlesDialogData } from "./achat-articles-dialog.component";

const C = {
  headerBg: "FF1B2A4A", headerFont: "FFFFFFFF",
  groupBg: "FFE8EDF3", groupFont: "FF1B2A4A",
  articleBg: "FFF5F7FA",
  lotBg: "FFFFFFFF", lotFont: "FF667085",
  border: "FFD0D5DD",
  titleColor: "FF1B2A4A",
  statGreen: "FFE6FFF5", statPurple: "FFF0E6FF", statOrange: "FFFFF0E0",
};

function colLetter(col: number): string {
  let s = "", c = col;
  while (c > 0) { c--; s = String.fromCharCode(65 + (c % 26)) + s; c = Math.floor(c / 26); }
  return s;
}
function cellRef(col: number, row: number): string { return `${colLetter(col)}${row}`; }

function headerStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = { bottom: { style: "medium", color: { argb: C.headerBg } } };
}
function statStyle(cell: ExcelJS.Cell, bg: string) {
  cell.font = { bold: true, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  const b = { style: "thin" as const, color: { argb: C.border } };
  cell.border = { top: b, bottom: b, left: b, right: b };
}

function fmtEur(n: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

export async function exportAchatArticlesExcel(data: AchatArticlesDialogData): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb);

  const ws = wb.addWorksheet("Articles achetés", { views: [{ state: "frozen", ySplit: 5 }] });

  const COL = { article: 1, commande: 2, lot: 3, date: 4, qty: 5, puAchat: 6, cout: 7, pct: 8 };
  [30, 18, 20, 12, 12, 14, 14, 10].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:H1");
  const tc = ws.getCell("A1");
  tc.value = `Articles achetés — ${data.tiersDenomination}`;
  tc.font = { bold: true, size: 14, color: { argb: C.titleColor } };
  ws.getRow(1).height = 26;

  const totalCout = data.articles.reduce((s, a) => s + a.coutTotal, 0);
  const totalQty = data.articles.reduce((s, a) => s + a.qtyUG, 0);
  const nbCommandes = data.articles.reduce((s, a) => s + a.commandes.length, 0);

  ws.mergeCells("A3:C3"); ws.mergeCells("D3:E3"); ws.mergeCells("F3:H3");
  const s1 = ws.getCell("A3");
  s1.value = `Coût total : ${fmtEur(totalCout)}`;
  statStyle(s1, C.statOrange);
  const s2 = ws.getCell("D3");
  s2.value = `${data.articles.length} articles · ${nbCommandes} commandes`;
  statStyle(s2, C.statGreen);
  const s3 = ws.getCell("F3");
  const totalUA = data.articles.reduce((s, a) => s + a.qtyUA, 0);
  const mainUA = data.articles[0]?.uniteAchat ?? "u";
  s3.value = `Qté achat : ${totalUA.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${mainUA}`;
  statStyle(s3, C.statPurple);
  ws.getRow(3).height = 26;

  const headers = ["Article", "N° Commande", "Lot / Unité", "Date", "Qté UG", "PU achat (€/UG)", "Coût (€)", "% coût"];
  headers.forEach((h, i) => { const c = ws.getCell(5, i + 1); c.value = h; headerStyle(c); });
  ws.getRow(5).height = 22;
  ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: 8 } };

  const eurFmt = '#,##0.00\\ "€"';
  let row = 6;
  const articleRowNumbers: number[] = [];

  const sorted = [...data.articles].sort((a, b) => b.coutTotal - a.coutTotal);

  for (const art of sorted) {
    const articleRowNum = row;
    articleRowNumbers.push(articleRowNum);
    row++;

    const commandeRowNumbers: number[] = [];

    for (const cmd of art.commandes) {
      const cmdRowNum = row;
      commandeRowNumbers.push(cmdRowNum);
      row++;

      const lotRowNumbers: number[] = [];

      for (const lot of cmd.lots) {
        const lr = ws.getRow(row);
        lr.getCell(COL.lot).value = lot.lotNumero ? `${lot.lotNumero} (${art.uniteGestion})` : art.uniteGestion;
        lr.getCell(COL.qty).value = lot.qtyUG;
        lr.getCell(COL.puAchat).value = lot.puUG;
        lr.getCell(COL.cout).value = { formula: `${cellRef(COL.qty, row)}*${cellRef(COL.puAchat, row)}`, result: lot.coutTotal } as any;

        for (let c = 1; c <= 8; c++) {
          lr.getCell(c).font = { size: 9, color: { argb: C.lotFont } };
          lr.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.lotBg } };
          lr.getCell(c).border = { bottom: { style: "hair", color: { argb: C.border } } };
        }
        lr.getCell(COL.qty).numFmt = "#,##0.00";
        [COL.puAchat, COL.cout].forEach((c) => (lr.getCell(c).numFmt = eurFmt));
        lotRowNumbers.push(row);
        row++;
      }

      // Ligne commande (agrégat)
      const cr = ws.getRow(cmdRowNum);
      cr.getCell(COL.commande).value = cmd.numero;
      cr.getCell(COL.date).value = cmd.dateKey ? new Date(cmd.dateKey) : "";
      cr.getCell(COL.date).numFmt = "dd/mm/yyyy";

      if (lotRowNumbers.length) {
        cr.getCell(COL.qty).value = {
          formula: `SUM(${lotRowNumbers.map((r) => cellRef(COL.qty, r)).join(",")})`,
          result: cmd.qtyUG,
        } as any;
        cr.getCell(COL.cout).value = {
          formula: `SUM(${lotRowNumbers.map((r) => cellRef(COL.cout, r)).join(",")})`,
          result: cmd.coutTotal,
        } as any;
      } else {
        cr.getCell(COL.qty).value = cmd.qtyUG;
        cr.getCell(COL.cout).value = cmd.coutTotal;
      }
      cr.getCell(COL.puAchat).value = {
        formula: `IF(${cellRef(COL.qty, cmdRowNum)}=0,0,${cellRef(COL.cout, cmdRowNum)}/${cellRef(COL.qty, cmdRowNum)})`,
        result: cmd.puUGMoyen,
      } as any;

      for (let c = 1; c <= 8; c++) {
        cr.getCell(c).font = { size: 10, italic: true, color: { argb: "FF334155" } };
        cr.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.articleBg } };
        cr.getCell(c).border = { bottom: { style: "hair", color: { argb: C.border } } };
      }
      cr.getCell(COL.qty).numFmt = "#,##0.00";
      [COL.puAchat, COL.cout].forEach((c) => (cr.getCell(c).numFmt = eurFmt));
    }

    // Ligne article (agrégat)
    const ar = ws.getRow(articleRowNum);
    ar.getCell(COL.article).value = `${art.articleDenomination} (${art.uniteGestion})`;

    if (commandeRowNumbers.length) {
      ar.getCell(COL.qty).value = {
        formula: `SUM(${commandeRowNumbers.map((r) => cellRef(COL.qty, r)).join(",")})`,
        result: art.qtyUG,
      } as any;
      ar.getCell(COL.cout).value = {
        formula: `SUM(${commandeRowNumbers.map((r) => cellRef(COL.cout, r)).join(",")})`,
        result: art.coutTotal,
      } as any;
    } else {
      ar.getCell(COL.qty).value = art.qtyUG;
      ar.getCell(COL.cout).value = art.coutTotal;
    }
    ar.getCell(COL.puAchat).value = {
      formula: `IF(${cellRef(COL.qty, articleRowNum)}=0,0,${cellRef(COL.cout, articleRowNum)}/${cellRef(COL.qty, articleRowNum)})`,
      result: art.puUGMoyen,
    } as any;

    for (let c = 1; c <= 8; c++) {
      ar.getCell(c).font = { bold: true, size: 10, color: { argb: C.groupFont } };
      ar.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
      ar.getCell(c).border = { top: { style: "thin", color: { argb: C.border } }, bottom: { style: "thin", color: { argb: C.border } } };
    }
    ar.getCell(COL.qty).numFmt = "#,##0.00";
    [COL.puAchat, COL.cout].forEach((c) => (ar.getCell(c).numFmt = eurFmt));
  }

  // Grand total
  row++;
  const gtRow = row;
  ws.mergeCells(gtRow, COL.article, gtRow, COL.date);
  const gtLabel = ws.getCell(gtRow, COL.article);
  gtLabel.value = "TOTAL GÉNÉRAL";
  gtLabel.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtLabel.alignment = { horizontal: "right", vertical: "middle" };

  if (articleRowNumbers.length) {
    ws.getCell(gtRow, COL.qty).value = {
      formula: `SUM(${articleRowNumbers.map((r) => cellRef(COL.qty, r)).join(",")})`,
      result: totalQty,
    } as any;
    ws.getCell(gtRow, COL.cout).value = {
      formula: `SUM(${articleRowNumbers.map((r) => cellRef(COL.cout, r)).join(",")})`,
      result: totalCout,
    } as any;
  }
  ws.getCell(gtRow, COL.pct).value = 1;

  for (let c = COL.qty; c <= COL.pct; c++) {
    ws.getCell(gtRow, c).font = { bold: true, size: 11, color: { argb: C.headerFont } };
    ws.getCell(gtRow, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  }
  ws.getCell(gtRow, COL.qty).numFmt = "#,##0.00";
  ws.getCell(gtRow, COL.cout).numFmt = eurFmt;
  ws.getCell(gtRow, COL.pct).numFmt = "0.0%";
  ws.getRow(gtRow).height = 26;

  // % coût sur les lignes article
  const gtRef = cellRef(COL.cout, gtRow);
  for (const r of articleRowNumbers) {
    ws.getCell(r, COL.pct).value = {
      formula: `IF(${gtRef}=0,0,${cellRef(COL.cout, r)}/${gtRef})`,
      result: 0,
    } as any;
    ws.getCell(r, COL.pct).numFmt = "0.0%";
    ws.getCell(r, COL.pct).font = { bold: true, size: 10, color: { argb: C.groupFont } };
    ws.getCell(r, COL.pct).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
  }

  await addJustupBranding(wb, ws, 8);

  const buffer = await wb.xlsx.writeBuffer() as ArrayBuffer;
  const blob = new Blob([new Uint8Array(buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const slug = (data.tiersDenomination || "tiers").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
  const filename = `achats-articles-${slug}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 10000);
}
